import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { formatUsd, messages, type Quota } from '@nesc/shared';
import { api, type SingleResponse } from '@/lib/api-client';
import { qk } from '@/lib/query-client';
import { Button } from '@/components/ui/button';

const DISMISS_KEY = 'quota-banner-dismissed';

/**
 * 上限警告バナー（docs/10-screen-spec.md 1.1）。
 * 80% 超過時のみ表示し、閉じても当日中は再表示しない。
 */
export const QuotaBanner = () => {
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(DISMISS_KEY) === new Date().toDateString(),
  );

  const { data } = useQuery({
    queryKey: qk.quota,
    queryFn: () => api.get<SingleResponse<Quota>>('/usage/quota'),
    staleTime: 5 * 60_000,
  });

  if (dismissed || !data) return null;

  const entries = [
    { key: 'ai', label: messages.quota.aiCost, entry: data.data.aiCost, used: data.data.aiCost.usedUsd, limit: data.data.aiCost.limitUsd, format: (v: number) => formatUsd(v) },
    { key: 'search', label: messages.quota.searchCalls, entry: data.data.search, used: data.data.search.usedCalls, limit: data.data.search.limitCalls, format: (v: number) => v.toLocaleString('ja-JP') },
    { key: 'send', label: messages.quota.dailySend, entry: data.data.send, used: data.data.send.usedToday, limit: data.data.send.dailyLimit, format: (v: number) => v.toLocaleString('ja-JP') },
  ].filter((item) => item.entry.level !== 'ok' && item.limit);

  if (entries.length === 0) return null;

  const worst = entries.find((e) => e.entry.level === 'blocked') ?? entries[0];
  const percent = worst.limit ? Math.round((worst.used / worst.limit) * 100) : 0;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, new Date().toDateString());
    setDismissed(true);
  };

  return (
    <div className="flex items-center gap-2 border-b-2 border-foreground bg-muted px-4 py-2 text-body lg:px-6">
      <AlertTriangle aria-hidden className="size-5 shrink-0" />
      <p className="min-w-0 flex-1 font-semibold">
        {worst.entry.level === 'blocked'
          ? `${worst.label}${messages.quota.blocked}`
          : `${worst.label}の${messages.quota.warning(percent)}`}
        <span className="tabular ml-2 font-normal text-foreground-muted">
          {messages.quota.usedOf(worst.format(worst.used), worst.format(worst.limit ?? 0))}
        </span>
      </p>
      <Button variant="outline" size="sm" onClick={() => navigate('/settings/limits')}>
        {messages.quota.openSettings}
      </Button>
      <Button variant="ghost" size="icon-sm" onClick={dismiss} aria-label="この警告を閉じる">
        <X aria-hidden className="size-4" />
      </Button>
    </div>
  );
};
